import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import styles from "./style";

// Why an Admin is locked out of a subscription-only page, or null when they are not.
// Same conditions SubscriptionAlert uses for its banner, so the two always agree.
// Only Admins carry these flags (Employees / platform staff never do), so only
// Admins are ever blocked.
export const getSubscriptionBlockReason = () => {
  if (localStorage.getItem("role") !== "Admin") return null;
  if (localStorage.getItem("isExpired") === "true") return "expired";
  if (localStorage.getItem("isTrialExpired") === "true") return "trialExpired";
  if (localStorage.getItem("is_subscription") === "false") return "none";
  return null;
};

const buildMessage = (reason, feature) => {
  if (reason === "expired") {
    return `Your subscription has expired. Renew your plan to use ${feature}.`;
  }
  if (reason === "trialExpired") {
    return `Your free trial has ended. Purchase a plan to use ${feature}.`;
  }
  return `Purchase a plan to use ${feature}.`;
};

// Wraps a route that needs an active subscription. This is a UX gate (it keeps the
// screen from opening); the backend is what actually refuses salary changes.
const SubscriptionRoute = ({ children, feature = "this page" }) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [reason, setReason] = useState(getSubscriptionBlockReason);

  useEffect(() => {
    const syncReason = () => setReason(getSubscriptionBlockReason());
    window.addEventListener("subscription_updated", syncReason);

    // The flags are only written at login and after a payment, so re-read the live
    // status on each visit. getProfileView() updates them and fires the event above.
    if (localStorage.getItem("role") === "Admin") {
      AuthService.getProfileView().catch(() => {});
    }

    return () => window.removeEventListener("subscription_updated", syncReason);
  }, []);

  const handleManagePlan = () => {
    if (String(localStorage.getItem("billing_info")) === "true") {
      navigate("/planManagement");
    } else {
      navigate("/dashboard/profile");
      showAlert("Please complete your Billing Information first", "info");
    }
  };

  if (!reason) {
    return children;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.card}>
        <LockOutlinedIcon sx={styles.icon} />
        <Typography sx={styles.heading}>{feature} is unavailable</Typography>
        <Typography sx={styles.message}>{buildMessage(reason, feature)}</Typography>
        <Box sx={styles.actions}>
          <Button variant="contained" onClick={handleManagePlan} sx={styles.primaryButton}>
            Manage Plan
          </Button>
          <Button variant="outlined" onClick={() => navigate("/dashboard")} sx={styles.secondaryButton}>
            Back to Dashboard
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SubscriptionRoute;
